import $ from 'jquery'
import KPopper, { PopperType, positions } from '../../core/mixins/popper/KPopper'
import ResizeAware from '../resize-aware'
import { getFirstComponentChild } from '../../core/utils/KVdom'
import { getCorrectEventName } from '../../core/utils'
import raf from 'raf'
import _ from 'lodash'
import { window } from 'ssr-window'

const transitionEnd = getCorrectEventName(window, 'transitionend')

export const TooltipType = {
  Default: 0,
  TooltipAction: 1,
}

export default {
  name: 'KTooltip',
  mixins: [KPopper],
  props: {
    type: {
      // `'primary'` / `'secondary'` / `'warning'` / `'danger'` / `'success'` / `'light'` / `'dark'`
      type: String,
      default: 'dark',
    },
    size: {
      type: String,
      default: '',
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    plain: {
      type: Boolean,
      default: false,
    },
    pre: {
      type: Boolean,
      default: true,
    },
    adjustOnChange: {
      type: Boolean,
      default: false,
    },
    align: {
      // `'left'` / `'center'` / `'right'`
      type: String,
      default: 'center',
    },
    destroyOnHide: {
      type: Boolean,
      default: true,
    },
    // 延迟显示 （单位：毫秒）
    delayShow: {
      type: Number,
      default: 100,
    },
    // 当触发元素或其父元素固定定位时，建议使用positionFixed:true，防止tooltip随着页面滚动调整
    positionFixed: {
      type: Boolean,
      default: false,
    },
    // 空间的层级低于任何弹出层，以KlowestNextZIndex作为z-index累加基础
    below: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      popperType: PopperType.TOOLTIP,
      tooltipType: TooltipType.Default,
    }
  },
  computed: {
    tooltipCss() {
      const css = {
        'km-tooltip': true,
        [`km-tooltip--${this.type}`]: true,
        'km-tooltip--outlined': this.outlined,
        'km-tooltip--plain': this.plain,
        'km-tooltip-action': this.tooltipType === TooltipType.TooltipAction,
        [`km-tooltip-align--${this.align}`]: true,
        'is-hidden': !this.activated,
        ...this.popperClassObjectLike,
      }
      if (this.size) {
        css[`is-${this.size}`] = true
      }
      // if (this.popperClassObjectLike) {
      //   _.forEach(this.popperClass.split(/\s+/), (className) => {
      //     css[className] = true
      //   })
      // }
      return css
    },
  },
  mounted() {
    this.reference = this.$el

    if (this.reference.nodeType === 1) {
      $(this.reference).attr({
        tabindex: 0,
        'aria-describedby': this.popperId,
      })
      this.bindTriggerEvents()
    }
  },
  render() {
    if (!this.popperVM) {
      return
    }

    let pre = false
    if (this.$slots.content) {
      pre = false
    } else if (this.content) {
      pre = this.pre
    }

    let content = null
    if (this.tooltipType === TooltipType.TooltipAction) {
      content = this.renderContent()
    } else {
      content = this.$slots.content || this.content
    }

    if (this.popperVM) {
      this.popperVM.node = (
        <transition
          onEnter={this.enter}
          onAfterEnter={this.afterEnter}
          onLeave={this.leave}
          onAfterLeave={this.afterLeave}
        >
          <div
            ref="popper"
            v-show={this.showPopper}
            style={{ ...this.positions }}
            class={{ ...this.tooltipCss }}
            onMouseenter={() => {
              this.opening = true
            }}
            onMousemove={() => {
              if (this.trigger === 'hover') {
                this.opening = true
              }
            }}
            onMouseleave={() => {
              if (this.trigger === 'hover') {
                this.hide()
              }
            }}
          >
            {pre ? <pre class="km-tooltip__inner">{content}</pre> : <div class="km-tooltip__inner">{content}</div>}
            <div
              x-arrow
              class="km-tooltip__arrow"
            />
            {
              this.adjustOnChange ? <ResizeAware onResize={this.handleResize} /> : null
            }
          </div>
        </transition>
      )
    }

    const reference = getFirstComponentChild(this.$slots.default)
    return reference
  },
  methods: {
    handleResize: _.debounce(function ({ width, height }) {
      this.popperEngine.scheduleUpdate()

      this.$emit('resize', { width, height })
    }, 50),
    getTransformOrigin() {
      if (!this.popperData) {
        return
      }
      const placement = this.popperData.placement
      const positionMeta = positions[placement]
      const origin = positionMeta ? positionMeta.origin(this.popperData) : 'center center'
      return origin
    },
    getTransformTranslate() {
      const popperElm = this.popperEngine.popper
      const placement = popperElm.getAttribute('x-placement').split('-')[0]
      const targetTransform = popperElm.style.transform // translate3d(${foo}px, ${bar}px, 0px)

      const translateOffset = 10
      const translate = {
        top: targetTransform.replace(
          /translate3d\((\d+)px, (\d+)px, 0px\)/,
          (all, $1, $2) => `translate3d(${$1}px, ${+$2 + translateOffset}px, 0px)`,
        ),
        bottom: targetTransform.replace(
          /translate3d\((\d+)px, (\d+)px, 0px\)/,
          (all, $1, $2) => `translate3d(${$1}px, ${+$2 - translateOffset}px, 0px)`,
        ),
        left: targetTransform.replace(
          /translate3d\((\d+)px, (\d+)px, 0px\)/,
          (all, $1, $2) => `translate3d(${+$1 + translateOffset}px, ${$2}px, 0px)`,
        ),
        right: targetTransform.replace(
          /translate3d\((\d+)px, (\d+)px, 0px\)/,
          (all, $1, $2) => `translate3d(${+$1 - translateOffset}px, ${$2}px, 0px)`,
        ),
      }[placement]

      return translate + ' scale(.1)'
    },
    enter(el, done) {
      raf(() => {
        this.targetTransform = el.style.transform
        el.style.transform = this.getTransformTranslate()
        el.style.transformOrigin = this.getTransformOrigin()
        el.style.opacity = 0
        el.style.transition = 'none'

        raf(() => {
          el.style.transform = this.targetTransform
          el.style.opacity = 1
          // el.style.transition = `transform .25s cubic-bezier(.39, 0, .45, 1.4), opacity .25s linear`
          el.style.transition = `transform .2s ease, opacity .2s linear`
          // el.style.transition = `transform 5s ease, opacity .25s linear`
          // done()
          $(el).one(transitionEnd, () => {
            // el.style.transition = 'none'
            done()
          })
        })
      })
    },
    afterEnter(el) {
      el.style.transition = 'none'
      el.style.transform = 'none'
    },
    leave(el, done) {
      raf(() => {
        el.style.transition = 'none'

        raf(() => {
          el.style.transform = this.targetTransform + ' scale(.1)'
          el.style.transition = `transform .15s cubic-bezier(.25, .8, .25, 1), opacity .15s linear`
          el.style.opacity = 0

          $(el).one(transitionEnd, () => {
            done()
          })
        })
      })
    },
    afterLeave(el) {
      el.style.transition = 'none'
      this.destroyPopper()
    },
  },
}
